import { defineStore } from "pinia";
import { threadApi, replyApi } from "@/api";
import type { ThreadDetail, CreateThreadParams, ApiResponse } from "@/api";
import { formatDateline } from "@/utils";
import { GAME_ID } from "./index";
import { useLanguageStore } from "./language";

// 板块类型定义
interface ForumSection {
  id?: number;
  name: string;
  fid: number | string;
  [key: string]: any;
}

// 回复标签枚举
export enum ReplyTab {
  All = "all", // 全部回复
  OnlyAuthor = "onlyAuthor", // 只看作者
}

// 回复排序方式枚举
export enum ReplySort {
  Earliest = "earliest", // 最早
  Latest = "latest", // 最新
}

// 处理后的帖子详情类型
interface ProcessedThreadDetail {
  avatar: string;
  username: string;
  date: string;
  title: string;
  content: string;
  images: string[];
  likes: number;
  comments: number;
  stars: number;
  isFollowed: boolean;
  isStarred: boolean;
  isLiked: boolean;
}

interface ReplyDetail {
  id: number;
  pid?: number;
  tid?: number; // 添加帖子ID字段
  authorid?: number;
  author?: string;
  avatarIndex?: number;
  username?: string;
  content?: string;
  message?: string;
  date?: string;
  dateline?: string;
  comments: number;
  stars: number;
  likes: number;
  isLiked: boolean;
  is_author_followed?: boolean;
  child_replies: any[];
}

export const useThreadStore = defineStore("thread", {
  state: () => ({
    // 当前帖子详情
    currentThread: null as ThreadDetail | null,
    currentReply: null as ReplyDetail | null,
    // 帖子回复列表
    replyList: [] as any[],
    replyDetailList: [] as any[],
    // 选择板块 - 改为直接存储板块对象
    selectedBoard: null as ForumSection | null,
    // 板块列表
    selectedBoardList: [] as ForumSection[],
    // 标签列表
    tagList: [] as any[],
    // 加载状态
    loading: false,
    // 错误信息
    error: null as string | null,
    // 分页信息
    pagination: null as ApiResponse.Pagination | null,
    replyDetailPagination: null as ApiResponse.Pagination | null,
    // 回复标签
    replyTab: ReplyTab.All,
    // 回复排序
    replySort: ReplySort.Earliest,
    // 用户帖子列表
    userThreads: [] as ThreadDetail[],
    // 用户帖子分页信息
    userThreadsPagination: null as ApiResponse.Pagination | null,
    // 用户帖子当前页码
    userThreadsPage: 1,
    // 加载更多状态
    loadingMore: false,
  }),

  getters: {
    // 获取当前帖子ID
    currentThreadId: (state) =>
      state.currentThread?.tid || state.currentReply?.tid,
    // 获取当前帖子作者
    currentThreadAuthor: (state) => state.currentThread?.author,
    // 获取当前帖子回复列表
    currentThreadReplies: (state) => state.currentThread?.replies || [],
    // 帖子回复数
    replyCount: (state) => state.replyList.length,
    // 获取选中的板块名称
    selectedBoardName: (state) => state.selectedBoard?.name || "",
    // 获取选中的板块ID
    selectedBoardFid: (state) => state.selectedBoard?.fid || "",
    // 是否有更多用户帖子
    hasMoreUserThreads: (state) =>
      state.userThreadsPagination?.has_next || false,
    // 自动格式化当前帖子详情
    formattedThreadDetail: (state) => {
      const t = state.currentThread;
      if (!t) return null;
      return {
        avatar: t.avatar || 0,
        uid: t.authorid || 0,
        username: t.author || "",
        date: t.dateline,
        title: t.subject || "",
        content: t.content || "",
        message: t.message || "",
        images: t.images || [],
        likes: t.likes || 0,
        comments: t.replies || 0,
        stars: t.favtimes || 0,
        isFollowed: t.is_author_followed || 0,
        isStarred: t.is_favorited || 0,
        isLiked: t.is_liked || 0,
        tid: t.tid,
        is_translated: t.is_translated || 0,
        tag_info: t.tag_info || [],
        avatar_frame: t.avatar_frame || "",
        user_title: t.user_title || [],
      };
    },
    formattedReplyList: (state) => {
      const authorUsername = state.currentThread?.author || "";
      return state.replyList.map((item) => ({
        id: Number(item.pid),
        avatarIndex: item.author_info.avatar || 1,
        username: item.author_info.username,
        authorid: item.authorid,
        avatar_frame: item.author_info?.avatar_frame || "",
        user_title: item.author_info?.user_title || [],
        floor: "", // 用floorIndex+1渲染
        content: item.content,
        message: item.message,
        child_replies: (item.child_replies || []).map((q: any) => ({
          username: q.author_info?.username || "",
          userId: q.author_info?.uid || 0,
          replyTo: q.target_user_info?.username || "",
          replyToId: q.target_user_info?.uid || 0,
          isAuthor: q.author_info?.username === authorUsername,
          isTargetAuthor: q.target_user_info?.username === authorUsername,
          content: q.message,
        })),
        date: item.dateline_formatted
          ? item.dateline_formatted.substring(5, 10)
          : "",
        likes: item.likes || 0,
        isLiked: item.is_liked,
      }));
    },
  },

  actions: {
    /**
     * 设置板块列表
     */
    setSelectedBoardList(boardList: ForumSection[]) {
      this.selectedBoardList = boardList;
    },

    /**
     * 设置选中的板块
     */
    setSelectedBoard(board: ForumSection) {
      this.selectedBoard = board;
    },

    /**
     * 获取帖子详情（支持分页回复）
     * @param tid 帖子ID
     * @param order_type 排序类型
     * @param author_only 只看作者
     * @param page 页码
     * @param page_size 每页数量
     * @param append 是否追加
     */
    async getThreadDetail(
      tid: number,
      order_type: number = 0,
      author_only: number = 0,
      page: number = 1,
      page_size: number = 10,
      append: boolean = false
    ) {
      this.loading = append ? false : true;
      this.error = null;
      try {
        // 假设后端支持分页参数
        const response = await threadApi.getDetail(
          tid,
          useLanguageStore().targetLanguage,
          order_type,
          author_only,
          page,
          page_size,
          GAME_ID
        );
        if (response.code == 200) {
          this.currentThread = response.data.thread;
          if (!append) {
            // 新请求或切页，直接覆盖
            this.replyList = response.data.posts || [];
          } else {
            // 追加更多评论
            this.replyList = [
              ...this.replyList,
              ...(response.data.posts || []),
            ];
          }
          // 更新分页信息
          this.pagination = response.data.pagination || null;
        }
        return response;
      } catch (error) {
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 加载更多回复
     */
    async loadMoreReplies(tid: number, page_size = 20) {
      if (!this.pagination?.has_next || this.loading) return;
      const nextPage = (this.pagination.current_page || 0) + 1;
      await this.getThreadDetail(
        tid,
        this.replySort === ReplySort.Latest ? 1 : 0,
        this.replyTab === ReplyTab.OnlyAuthor ? 1 : 0,
        nextPage,
        page_size,
        true
      );
    },

    /**
     * 发布帖子
     * @param fid 论坛分区ID
     * @param tag_id 标签ID（可以是单个ID或逗号分隔的多个ID）
     * @param subject 帖子标题
     * @param message 帖子内容
     * @param language 语言
     */
    async createThread(
      fid: number,
      tag_id: number | string,
      subject: string,
      message: string,
      language: string
    ) {
      this.error = null;
      try {
        const response = await threadApi.create(
          fid,
          tag_id,
          subject,
          message,
          GAME_ID,
          language
        );
        return response;
      } catch (error) {
        this.error = error instanceof Error ? error.message : "发布帖子失败";
        throw error;
      }
    },

    /**
     * 评论帖子
     * @param tid 帖子ID
     * @param content 评论内容
     * @param language 语言
     */
    async createReply(tid: number, content: string, language: string) {
      this.error = null;
      try {
        const response = await replyApi.create(tid, content, GAME_ID, language);
        if (response.code == 200) {
          await this.getThreadDetail(tid);
        }
        return response;
      } catch (error) {
        this.error = error instanceof Error ? error.message : "评论失败";
        throw error;
      }
    },

    /**
     * 回复帖子中的评论
     * @param tid 帖子ID
     * @param content 回复内容
     * @param replyId 被回复的评论ID
     * @param language 语言
     */
    async replyToComment(
      tid: number,
      content: string,
      replyId: number,
      language: string
    ) {
      const response = await replyApi.replyToComment(
        tid,
        content,
        replyId,
        GAME_ID,
        language
      );
      if (response.code == 200) {
        if (this.currentReply) {
          await this.getReplyDetail(this.currentReply.pid);
        } else {
          await this.getThreadDetail(tid);
        }
      }
      return response;
    },

    /**
     * 获取回复详情
     */
    async getReplyDetail(toppid: number) {
      const response = await replyApi.getReplyListByTid(toppid, GAME_ID);
      if (response.code == 200) {
        this.replyDetailList = response.data.replies || [];
        this.replyDetailPagination = response.data.pagination || null;
        this.currentReply = response.data.first_reply || {};
      }
      return response;
    },

    /**
     * 获取用户帖子列表
     */
    async getUserThreads(
      uid: number | string,
      page: number = 1,
      page_size: number = 10,
      append: boolean = false
    ) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }

      try {
        const response = await threadApi.getUserThreads(
          uid,
          useLanguageStore().targetLanguage,
          page,
          page_size,
          GAME_ID
        );
        if (response.code == 200) {
          const threads = response.data.threads || [];
          if (append) {
            // 追加模式：将新数据添加到现有列表
            this.userThreads = [...this.userThreads, ...threads];
          } else {
            // 非追加模式：替换现有列表
            this.userThreads = threads;
          }

          // 更新分页信息
          this.userThreadsPagination = response.data.pagination || null;
          this.userThreadsPage = page;
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取用户帖子列表失败";
        throw error;
      } finally {
        if (append) {
          this.loadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },

    /**
     * 加载更多用户帖子
     */
    async loadMoreUserThreads(uid: number | string) {
      if (
        !this.userThreadsPagination?.has_next ||
        this.loading ||
        this.loadingMore
      ) {
        return;
      }

      const nextPage = this.userThreadsPage + 1;
      await this.getUserThreads(uid, nextPage, 10, true);
    },

    /**
     * 更新回复
     */
    updateReplyInList(id: number, updates: Partial<any>) {
      const index = this.replyList.findIndex((reply) => reply.id === id);
      if (index !== -1) {
        this.replyList[index] = { ...this.replyList[index], ...updates };
      }
    },

    /**
     * 更新帖子关注状态
     */
    updateFollowStatus(tid: number, isFollowed: boolean) {
      this.currentThread.is_author_followed = isFollowed;
    },

    /**
     * 更新帖子点赞状态
     */
    updateLikeStatus(tid: number, isLiked: boolean) {
      this.currentThread.is_liked = isLiked;
      this.currentThread.likes = isLiked
        ? this.currentThread.likes + 1
        : this.currentThread.likes - 1;
    },

    /**
     * 更新回复点赞状态
     */
    updateReplyLikeStatus(
      id: number,
      isLiked: boolean,
      isReplyDetail: boolean = false
    ) {
      if (isReplyDetail) {
        // 更新回复详情列表中的点赞状态
        const reply = this.replyDetailList.find((reply) => +reply.pid === id);
        if (reply) {
          reply.is_liked = isLiked;
          reply.likes = isLiked ? +reply.likes + 1 : +reply.likes - 1;
        }
      } else {
        // 更新普通回复列表中的点赞状态
        const reply = this.replyList.find((reply) => +reply.pid === id);
        if (reply) {
          reply.is_liked = isLiked;
          reply.likes = isLiked ? +reply.likes + 1 : +reply.likes - 1;
        }
      }
    },

    /**
     * 更新帖子收藏状态
     */
    updateStarStatus(tid: number, isStarred: boolean) {
      this.currentThread.is_favorited = isStarred;
      this.currentThread.favtimes = isStarred
        ? this.currentThread.favtimes + 1
        : this.currentThread.favtimes - 1;
    },

    /**
     * 切换回复标签
     */
    async switchReplyTab(tab: ReplyTab) {
      this.replyTab = tab;
      if (this.currentThread?.tid) {
        this.reset();
        // 切换标签时重新请求数据,重置到第一页
        await this.getThreadDetail(
          this.currentThread.tid,
          this.replySort === ReplySort.Latest ? 1 : 0,
          this.replyTab === ReplyTab.OnlyAuthor ? 1 : 0,
          1
        );
      }
    },

    /**
     * 切换回复排序
     */
    async switchReplySort(sort: ReplySort) {
      this.replySort = sort;
      if (this.currentThread?.tid) {
        // 切换排序时重新请求数据,重置到第一页
        this.reset();
        await this.getThreadDetail(
          this.currentThread.tid,
          this.replySort === ReplySort.Latest ? 1 : 0,
          this.replyTab === ReplyTab.OnlyAuthor ? 1 : 0,
          1
        );
      }
    },

    /**
     * 重置帖子选项
     */
    resetThreadDetail() {
      this.replyTab = ReplyTab.All;
      this.replySort = ReplySort.Earliest;
    },

    /**
     * 删除帖子
     */
    async deleteThread(tid: number) {
      const response = await threadApi.delete(tid, GAME_ID);
      if (response.code == 200) {
        return response;
      }
    },

    /**
     * 删除回复
     */
    async deleteReply(pid: number) {
      const response = await replyApi.delete(pid, GAME_ID);
      return response;
    },

    /**
     * 获取标签列表
     */
    async getTagList() {
      const response = await threadApi.getTagInfo(
        GAME_ID,
        useLanguageStore().targetLanguage
      );
      if (response.code == 200) {
        this.tagList = response.data || [];
      }
    },

    /**
     * 重置store状态
     */
    reset() {
      this.loading = true;
      this.pagination = null;
      this.currentReply = null;
    },
  },
});
